import uniqueId from 'lodash-es/uniqueId';
import React, { useRef } from 'react';
import { shallowEqual } from 'react-redux';
import { MdKeyboardArrowRight, MdKeyboardArrowDown } from '@meronex/icons/md/';
import { Box } from '@apia/theme';
import { IconButton } from '@apia/components';
import { getVariant } from '@apia/theme';
import { domPropsSelector } from './ResponsiveTable';
import { TResponsiveTableCell, TResponsiveTableColumn } from './types';
import { AdditionalColumnDefaultRenderer } from './renderers/AdditionalColumnDefaultRenderer';
import { SeparatorCell } from './SeparatorCell';
import { DefaultRowRenderer } from './renderers/DefaultRowRenderer';
import { useResponsiveTableContext } from './ResponsiveTableContext';
import {
  responsiveTableActions,
  responsiveTableStore,
  useResponsiveTable,
} from './store';
import { useDomProps } from './store/selection/useDomProps';
import { StateCell } from './StateCell';
import { Cell } from './Cell';
import { getLabel, scrollParentIntoElement } from '@apia/util';

interface IRow {
  rowIndex: number;
}

type TAdditionalCell = {
  cell: TResponsiveTableCell;
  column: TResponsiveTableColumn;
  columnIndex: number;
};

const NoMemoRow = ({ rowIndex }: IRow) => {
  const { name } = useResponsiveTableContext();

  const { columns, hasNonAdditionalFilters, isScrolledIntoView } =
    useResponsiveTable((global) => {
      const tableState = global.responsiveTableSlice[name];
      return {
        columns: tableState?.columns ?? [],
        hasNonAdditionalFilters: tableState?.hasNonAdditionalFilters,
        isScrolledIntoView: tableState.scrollIntoViewRow === rowIndex,
      };
    }, shallowEqual);
  const row = useResponsiveTable(
    (global) => global.responsiveTableSlice[name].rows[rowIndex],
    shallowEqual,
  );
  const additionalCells: TAdditionalCell[] = useResponsiveTable(
    (global) => {
      const tableState = global.responsiveTableSlice[name];
      return tableState.columns
        .map((current, index) =>
          current.showAsAdditional
            ? {
                index,
                column: current,
              }
            : null,
        )
        .filter(
          (
            current,
          ): current is {
            index: number;
            column: TResponsiveTableColumn;
          } => !!current,
        )
        .map((current) => {
          return {
            cell: tableState.rows[rowIndex]?.cells[current.index],
            column: current.column,
            columnIndex: current.index,
          };
        });
    },
    (currentValue, newValue) => {
      return currentValue.reduce<boolean>(
        (accumulatedBoolean, currentCell, cellIndex) => {
          return (
            accumulatedBoolean && shallowEqual(currentCell, newValue[cellIndex])
          );
        },
        true,
      );
    },
  );
  const { isDragging, isExpanded, shouldForbidSelection } = useResponsiveTable(
    (global) => {
      const tableState = global.responsiveTableSlice[name];
      return {
        isExpanded: tableState.expandedRows.includes(rowIndex),
        isDragging: tableState.draggingRow === rowIndex,
        shouldForbidSelection: tableState.rows[rowIndex]?.forbidSelection,
      };
    },
    shallowEqual,
  );

  const domProps = useDomProps(
    'tr',
    {
      isThisRowExpanded: isExpanded,
      rowIndex,
    },
    domPropsSelector(name),
  );

  const expandedRowDomProps = useDomProps(
    'tr',
    {
      isTheExpandedRow: true,
      rowIndex,
    },
    domPropsSelector(name),
  );

  const handleExpandedState = React.useCallback(
    (ev: boolean | React.MouseEvent) => {
      const willBeExpanded = typeof ev === 'boolean' ? ev : !isExpanded;
      if (!willBeExpanded)
        responsiveTableStore.dispatch(
          responsiveTableActions.update({
            tableName: name,
            expandedRows: responsiveTableStore
              .getState()
              .responsiveTableSlice[
                name
              ].expandedRows.filter((current) => current !== rowIndex),
          }),
        );
      else
        responsiveTableStore.dispatch(
          responsiveTableActions.update({
            tableName: name,
            expandedRows: [
              ...responsiveTableStore.getState().responsiveTableSlice[name]
                .expandedRows,
              rowIndex,
            ],
          }),
        );
    },
    [isExpanded, name, rowIndex],
  );

  const statesCount = useResponsiveTable((globalState) => {
    const state = globalState.responsiveTableSlice[name];
    return state?.statesColumns ?? 0;
  }, shallowEqual);

  const states = React.useMemo(
    () => (row.states ?? []).map((current) => ({ ...current, id: uniqueId() })),
    [row.states],
  );

  const { ref, ...additionalCellDomProps } = useDomProps(
    'additionalTd',
    { rowIndex },
    domPropsSelector(name),
  );

  const Renderer = React.useMemo(
    () => row.renderer ?? DefaultRowRenderer,
    [row.renderer],
  );

  const hasScrolled = useRef(false);

  return (
    <>
      <Renderer
        {...domProps}
        row={row}
        rowIndex={rowIndex}
        className={`responsiveTable__row ${row.isHidden ? 'hidden' : ''} ${
          domProps['aria-selected'] ? 'selected' : ''
        } ${shouldForbidSelection ? 'non-selectable' : ''} ${states
          .map((current) => current.className)
          .join(' ')} ${row.className ?? ''} ${
          row.separator ? 'row__separator' : ''
        } ${isDragging ? 'draggingRow' : ''}`}
        onKeyDown={React.useCallback(
          (ev: React.KeyboardEvent) => {
            if (ev.key.toLowerCase() === 'arrowleft' && ev.ctrlKey) {
              ev.preventDefault();
              ev.stopPropagation();
              handleExpandedState(false);
            }
            if (ev.key.toLowerCase() === 'arrowright' && ev.ctrlKey) {
              ev.preventDefault();
              ev.stopPropagation();
              handleExpandedState(true);
            }
          },
          [handleExpandedState],
        )}
        {...{
          ref: (el: HTMLElement) => {
            if (el && isScrolledIntoView && !hasScrolled.current) {
              hasScrolled.current = true;
              scrollParentIntoElement(el);
            }
          },
        }}
      >
        {row.separator ? (
          <SeparatorCell
            colSpan={
              (additionalCells.length > 0 || hasNonAdditionalFilters ? 1 : 0) +
              statesCount +
              columns.filter((current) => !current.showAsAdditional).length
            }
            rowIndex={rowIndex}
          />
        ) : (
          <>
            {(additionalCells.length > 0 || hasNonAdditionalFilters) && (
              <td className="additionalInfo__cell" {...additionalCellDomProps}>
                {additionalCells.length > 0 && (
                  <IconButton
                    size={'Lg'}
                    {...getVariant('icon-outline')}
                    icon={
                      isExpanded ? MdKeyboardArrowDown : MdKeyboardArrowRight
                    }
                    aria-label={
                      isExpanded
                        ? getLabel('lblCollapseRow').text
                        : getLabel('lblExpandRow').text
                    }
                    className="moreInformationButton"
                    onClick={handleExpandedState}
                  />
                )}
              </td>
            )}
            {Array(statesCount)
              .fill('')
              .map((_, i) => {
                return (
                  <StateCell
                    key={states[i]?.id ?? i}
                    index={i}
                    state={states[i]}
                    rowIndex={rowIndex}
                  />
                );
              })}
            {columns
              .map((current, actualColumnIndex) => ({
                ...current,
                actualColumnIndex,
              }))
              .filter((current) => !current.showAsAdditional)
              .map((current, inTableColumnIndex) => {
                if (current.hidden) return null;
                return (
                  <Cell
                    key={current.name}
                    columnIndex={current.actualColumnIndex}
                    rowIndex={rowIndex}
                    inTableColumnIndex={inTableColumnIndex}
                  />
                );
              })}
          </>
        )}
      </Renderer>
      {isExpanded && (
        <tr className="expandedRow" {...expandedRowDomProps}>
          <td
            colSpan={
              columns.length - additionalCells.length + states.length + 1
            }
          >
            <Box className="responsiveTable__additionalInfoContainer">
              {additionalCells.map((current) => {
                const { Renderer: ColumnRenderer } = current.column;
                if (ColumnRenderer)
                  return (
                    <ColumnRenderer
                      key={current.column.name}
                      cell={current.cell}
                      column={current.column}
                      row={row}
                    />
                  );
                return (
                  <AdditionalColumnDefaultRenderer
                    key={current.column.name}
                    cell={current.cell}
                    column={current.column}
                    row={row}
                  />
                );
              })}
            </Box>
          </td>
        </tr>
      )}
    </>
  );
};

export const Row = React.memo(NoMemoRow);
